package com.merkost.handpass.sharedweb.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.sharedweb.theme.Dur
import com.merkost.handpass.sharedweb.theme.Ease
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.HandpassTypography
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusPill
import com.merkost.handpass.sharedweb.theme.Surface
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.theme.TouchTarget
import com.merkost.handpass.sharedweb.theme.TrackingHeading
import com.merkost.handpass.sharedweb.theme.WebSize
import com.merkost.handpass.sharedweb.theme.WebIconSize
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text

enum class WebStateKind { Empty, Error, Unavailable, Offline, NotFound }
enum class WebStateHeading { H1, H2, H3 }

private data class WebStateVisual(
    val icon: WebIcon,
)

private fun WebStateKind.visual() = when (this) {
    WebStateKind.Empty -> WebStateVisual(WebIcon.Package)
    WebStateKind.Error -> WebStateVisual(WebIcon.Alert)
    WebStateKind.Unavailable -> WebStateVisual(WebIcon.Clock)
    WebStateKind.Offline -> WebStateVisual(WebIcon.Globe)
    WebStateKind.NotFound -> WebStateVisual(WebIcon.Search)
}

val WebStatePanelStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Column)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.Center)
        .padding(HandpassSpacing.XXXL)
        .styleModifier {
            property("text-align", "center")
            property("min-height", WebSize.P240.value().toString())
        }
}

@Composable
fun WebStatePanel(
    kind: WebStateKind,
    title: String,
    message: String? = null,
    actionLabel: String? = null,
    onAction: (() -> Unit)? = null,
    compact: Boolean = false,
    icon: WebIcon? = null,
    heading: WebStateHeading? = null,
) {
    val visual = kind.visual()
    Div(
        attrs = WebStatePanelStyle.toModifier()
            .then(
                if (compact) {
                    Modifier
                        .padding(HandpassSpacing.MD)
                        .styleModifier { property("min-height", "auto") }
                } else {
                    Modifier
                }
            )
            .toAttrs {
                attr("role", if (kind == WebStateKind.Error || kind == WebStateKind.Offline) "alert" else "status")
            }
    ) {
        Div(
            attrs = Modifier
                .display(DisplayStyle.Flex)
                .alignItems(AlignItems.Center)
                .justifyContent(JustifyContent.Center)
                .backgroundColor(SurfaceMuted.value())
                .borderRadius(WebSize.P14.value())
                .color(OnSurfaceMuted.value())
                .width(WebSize.P48.value())
                .height(WebSize.P48.value())
                .styleModifier { property("margin-bottom", WebSize.P12.value().toString()) }
                .toAttrs()
        ) {
            Icon(icon = icon ?: visual.icon, size = WebIconSize.P20)
        }
        val isPageTitle = heading == WebStateHeading.H1
        val titleModifier = Modifier
                .color(OnSurface.value())
                .fontWeight(if (isPageTitle) 800 else 600)
                .styleModifier {
                    property(
                        "font-size",
                        if (isPageTitle) {
                            HandpassTypography.XL.toString()
                        } else {
                            HandpassTypography.MD.toString()
                        },
                    )
                    property("line-height", if (isPageTitle) "1.2" else "1.35")
                    if (isPageTitle) property("letter-spacing", TrackingHeading.value())
                    property("margin", "0 0 ${WebSize.P6.value()}")
                }
        when (heading) {
            WebStateHeading.H1 -> H1(attrs = titleModifier.toAttrs()) { Text(title) }
            WebStateHeading.H2 -> H2(attrs = titleModifier.toAttrs()) { Text(title) }
            WebStateHeading.H3 -> H3(attrs = titleModifier.toAttrs()) { Text(title) }
            null -> Div(attrs = titleModifier.toAttrs()) { Text(title) }
        }
        if (message != null) {
            Div(
                attrs = Modifier
                    .color(OnSurfaceMuted.value())
                    .styleModifier {
                        property("font-size", HandpassTypography.Body.toString())
                        property("line-height", "1.5")
                        property("max-width", "40ch")
                        property("margin-bottom", WebSize.P16.value().toString())
                    }
                    .toAttrs()
            ) {
                Text(message)
            }
        }
        if (actionLabel != null && onAction != null) {
            Button(
                attrs = Modifier
                    .display(DisplayStyle.Flex)
                    .alignItems(AlignItems.Center)
                    .justifyContent(JustifyContent.Center)
                    .backgroundColor(OnSurface.value())
                    .borderRadius(RadiusPill.value())
                    .color(Surface.value())
                    .fontWeight(600)
                    .padding(WebSize.P0.value(), WebSize.P18.value())
                    .styleModifier {
                        property("min-height", TouchTarget.value().toString())
                        property("border", "none")
                        property("font-family", HandpassTypography.FontFamily)
                        property("font-size", HandpassTypography.Body.toString())
                        property("cursor", "pointer")
                        property("transition", "opacity ${Dur.Fast} ${Ease.Standard}, transform ${Dur.Press} ${Ease.Standard}")
                    }
                    .toAttrs {
                        classes("hp-pressable")
                        attr("type", "button")
                        onClick { onAction() }
                    }
            ) {
                Text(actionLabel)
            }
        }
    }
}

@Composable
fun WebEmptyState(
    icon: WebIcon,
    title: String,
    message: String? = null,
    actionLabel: String? = null,
    onAction: (() -> Unit)? = null,
) {
    WebStatePanel(
        kind = WebStateKind.Empty,
        icon = icon,
        title = title,
        message = message,
        actionLabel = actionLabel,
        onAction = onAction,
    )
}
